/** @jsxImportSource preact */
import { createContext, type ComponentChildren } from "preact";
import { useContext, useEffect, useRef, useState } from "preact/hooks";

import { NoticeToast } from "../form/notice-toast";
import type { WidgetTokens } from "../theme";

interface SupportNotice {
  id: number;
  /** 产生这条提示时的会话与身份；作用域变了就不再显示，不靠事后清理。 */
  scope: string;
  text: string;
  title?: string;
  duration?: number;
  tone: "default" | "danger";
}
interface SupportFeedback {
  notice?: SupportNotice;
  show: (
    text: string,
    tone?: SupportNotice["tone"],
    options?: { title?: string; duration?: number },
  ) => void;
  close: () => void;
}
const SupportFeedbackContext = createContext<SupportFeedback | undefined>(
  undefined,
);

/** 只保存当前主动操作的反馈，不进会话快照。 */
export function SupportFeedbackProvider({
  children,
  scope = "",
}: {
  scope?: string;
  children: ComponentChildren;
}) {
  const [notice, setNotice] = useState<SupportNotice>();
  const sequence = useRef(0);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => {
    if (!notice?.duration) return;
    const timer = setTimeout(() => {
      setNotice((current) => (current?.id === notice.id ? undefined : current));
    }, notice.duration);
    return () => {
      clearTimeout(timer);
    };
  }, [notice]);
  return (
    <SupportFeedbackContext.Provider
      value={{
        notice: notice?.scope === scope ? notice : undefined,
        show: (text, tone = "default", options) => {
          if (currentScope.current !== scope) return;
          setNotice({ id: ++sequence.current, scope, text, tone, ...options });
        },
        close: () => {
          setNotice((current) =>
            current?.id === notice?.id ? undefined : current,
          );
        },
      }}
    >
      {children}
    </SupportFeedbackContext.Provider>
  );
}
export const useSupportFeedback = () => useContext(SupportFeedbackContext);

/** 一个面板只挂一个浮层，卡片状态和输入不受关闭影响。 */
export function SupportNotice({ tokens }: { tokens: WidgetTokens }) {
  const feedback = useSupportFeedback();
  return feedback?.notice ? (
    <NoticeToast
      key={feedback.notice.id}
      tokens={tokens}
      tone={feedback.notice.tone}
      title={feedback.notice.title}
      onClose={feedback.close}
    >
      {feedback.notice.text || undefined}
    </NoticeToast>
  ) : null;
}
